import React, { useState } from 'react';
import { motion, type Variants, type Transition } from 'framer-motion';
import { StatusBadge } from './StatusBadge';
import { IncidentDetails } from './IncidentDetails';
import type { Incident } from '../types';
import { formatTimeAgo, formatDuration } from '../utils';
import { INCIDENT_TYPE_CONFIG } from '../constants';

const springTransition: Transition = {
    type: "spring",
    stiffness: 100,
    damping: 12,
    mass: 0.8
};

const exitTransition: Transition = {
    type: "tween",
    duration: 0.2,
    ease: "easeInOut"
};

const cardVariants: Variants = {
    hidden: { 
        opacity: 0, 
        y: 20, 
        scale: 0.95 
    },
    visible: { 
        opacity: 1, 
        y: 0, 
        scale: 1,
        transition: springTransition
    },
    exit: { 
        opacity: 0, 
        x: -20,
        transition: exitTransition
    }
};

interface IncidentCardProps {
    incident: Incident;
    index?: number;
}

export const IncidentCard: React.FC<IncidentCardProps> = ({
    incident,
    index = 0
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const typeConfig = INCIDENT_TYPE_CONFIG[incident.type];

    const staggerDelay = index * 0.05;

    const icons: Record<string, React.ReactNode> = {
        'alert-circle': (
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
        ),
        'alert-triangle': (
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
        ),
        'check-circle': (
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
        ),
        'info': (
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
        )
    };

    const cardClasses = `
        bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl overflow-hidden
        transition-all duration-200
        ${isExpanded ? 'ring-1 ring-[var(--brand)] shadow-lg' : 'hover:border-[var(--border-strong)]'}
    `.trim();

    return (
        <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            layout
            transition={{ delay: staggerDelay }}
            className={cardClasses}
            style={{
                boxShadow: isExpanded ? '0 10px 15px -3px rgba(0,0,0,0.1)' : undefined
            }}
        >
            <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-start gap-3 p-4 text-left select-none"
                aria-expanded={isExpanded}
            >
                <motion.div
                    whileHover={{ scale: 1.05 }}
                    className={`
                        w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                        ${incident.status === 'active' ? 'animate-pulse' : ''}
                    `}
                    style={{
                        backgroundColor: typeConfig.bgColor,
                        color: typeConfig.color
                    }}
                >
                    {icons[typeConfig.icon]}
                </motion.div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="text-[14px] font-semibold text-[var(--text)] leading-tight">
                            {incident.title}
                        </h3>
                        <time 
                            className="text-[11px] text-[var(--text-muted)] font-mono flex-shrink-0"
                            dateTime={incident.createdAt}
                        >
                            {formatTimeAgo(incident.createdAt)}
                        </time>
                    </div>
                    
                    <p className="text-[12px] text-[var(--text-secondary)] line-clamp-2 mb-2">
                        {incident.meta.siteName} · {incident.meta.siteUrl.replace(/^https?:\/\//, '')}
                        {incident.meta.duration && ` · ${formatDuration(incident.meta.duration)}`}
                    </p>

                    <div className="flex items-center gap-2 flex-wrap">
                        <StatusBadge 
                            type={incident.type} 
                            status={incident.status}
                            showPulse={incident.status === 'active'}
                            size="sm"
                        />
                        {incident.assignedTo && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--brand-dim)] text-[var(--brand)]">
                                {incident.assignedTo}
                            </span>
                        )}
                    </div>
                </div>

                <motion.div
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="text-[var(--text-muted)] flex-shrink-0 mt-1"
                    aria-hidden="true"
                >
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <polyline points="6 9 12 15 18 9"/>
                    </svg>
                </motion.div>
            </button>

            <IncidentDetails incident={incident} isOpen={isExpanded} />
        </motion.div>
    );
};